/**
 * Applies user reader settings (margin, animation, tap-zone) as CSS inside
 * each epub.js spine iframe. Registered via rendition.hooks.content.
 *
 * Companion to useEpubLayout (which drives rendition-level spread/flow).
 * Settings captured here are iframe-content-level: page margins (padding on
 * body) and animation duration (transition timing on body transforms).
 */

import type { PageMargin, PageAnimation, TapZoneMode } from './types';

interface EpubJsContents {
  document: Document;
  window: Window;
}

export interface IframeReaderSettings {
  pageMargin: PageMargin;
  pageAnimation: PageAnimation;
  tapZoneMode: TapZoneMode;
}

// ADJUST these keys if the enum integer values in types.ts differ.
// Actual values confirmed: PageMargin { Medium=0, Narrow=1, Wide=2 }
const MARGIN_PX: Record<number, number> = {
  0: 24, // Medium
  1: 12, // Narrow
  2: 48, // Wide
};

// Actual values confirmed: PageAnimation { Slide=0, Fade=1, None=2 }
const ANIMATION_MS: Record<number, number> = {
  0: 250, // Slide
  1: 200, // Fade
  2: 0,   // None
};

export function createReaderSettingsHook(getSettings: () => IframeReaderSettings) {
  return function applyReaderSettings(contents: EpubJsContents): void {
    const s = getSettings();
    const doc = contents.document;

    const marginPx = MARGIN_PX[s.pageMargin as unknown as number] ?? 24;
    const animMs = ANIMATION_MS[s.pageAnimation as unknown as number] ?? 250;

    const style = doc.createElement('style');
    style.setAttribute('data-reader-settings', '1');
    style.textContent = `
      html, body {
        padding: 0 ${marginPx}px !important;
        transition: transform ${animMs}ms ease-in-out;
      }
    `;
    doc.head.appendChild(style);
  };
}
